/**
 * Environmental — per-type preferences sub-form (Workflow Prompt 2).
 * Required: expectedNepaPathway (drives the regulatory complexity branch).
 */
import React from 'react';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Label } from '../../ui/label';
import type { EnvironmentalPreferences } from '../../../types/siteRag';
import { FieldRow } from './shared';
import { validateEnvironmentalPreferences } from '../../../lib/reportPreferences/validation';
// Pure validator lives in lib so server code can use it without this form.
export { validateEnvironmentalPreferences };

interface Props {
  value: EnvironmentalPreferences | undefined;
  onChange: (next: EnvironmentalPreferences) => void;
}

export const EnvironmentalPreferencesForm: React.FC<Props> = ({ value: rawValue, onChange }) => {
  // PB2: belt-and-braces.
  const value: EnvironmentalPreferences = (rawValue ?? {}) as EnvironmentalPreferences;
  const set = <K extends keyof EnvironmentalPreferences>(key: K, v: EnvironmentalPreferences[K]) => {
    onChange({ ...value, [key]: v });
  };
  const speciesText = (value.speciesConcerns ?? []).join(', ');
  const setSpecies = (raw: string) => {
    const items = raw.split(',').map(s => s.trim()).filter(Boolean);
    set('speciesConcerns', items.length > 0 ? items : undefined);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FieldRow label="Expected NEPA pathway" fieldKey="expectedNepaPathway" required help="CatEx = categorical exclusion. EA = environmental assessment. EIS = full impact statement. Sets the analysis tone.">
          <Select value={value.expectedNepaPathway ?? ''} onValueChange={(v) => set('expectedNepaPathway', v as EnvironmentalPreferences['expectedNepaPathway'])}>
            <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="catex">CatEx</SelectItem>
              <SelectItem value="ea">Environmental Assessment</SelectItem>
              <SelectItem value="eis">Environmental Impact Statement</SelectItem>
              <SelectItem value="unknown">Unknown / TBD</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Air basin override" fieldKey="airBasinOverride" help="Force the air-quality analysis to use a specific attainment status. Auto = use the EPA AirNow data for the location.">
          <Select value={value.airBasinOverride ?? ''} onValueChange={(v) => set('airBasinOverride', v as EnvironmentalPreferences['airBasinOverride'])}>
            <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Auto" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto (use EPA data)</SelectItem>
              <SelectItem value="attainment">Attainment</SelectItem>
              <SelectItem value="non-attainment-marginal">Non-attainment — marginal</SelectItem>
              <SelectItem value="non-attainment-serious">Non-attainment — serious/severe</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Discharge water target" fieldKey="dischargeWaterTarget" help="NPDES = standard regulated discharge. ZLD = zero liquid discharge. Reuse = on-site reuse.">
          <Select value={value.dischargeWaterTarget ?? ''} onValueChange={(v) => set('dischargeWaterTarget', v as EnvironmentalPreferences['dischargeWaterTarget'])}>
            <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="npdes">NPDES</SelectItem>
              <SelectItem value="zld">ZLD</SelectItem>
              <SelectItem value="reuse">Reuse</SelectItem>
              <SelectItem value="unknown">Unknown / TBD</SelectItem>
            </SelectContent>
          </Select>
        </FieldRow>

        <FieldRow label="Species concerns" fieldKey="speciesConcerns" help="Comma-separated list of ESA-listed species you want emphasized in the ecological assessment. Leave empty for the default screening.">
          <Input
            type="text"
            placeholder="e.g. Indiana bat, Northern long-eared bat"
            value={speciesText}
            onChange={(e) => setSpecies(e.target.value)}
            className="h-9 text-sm"
          />
        </FieldRow>
      </div>

      <div className="border-t border-border pt-4 space-y-3">
        <div className="flex items-center justify-between" data-testid="pref-field-federalNexus">
          <div>
            <Label className="type-section text-text-secondary">Federal nexus</Label>
            <div className="text-[10px] text-text-muted">
              Federal funding, federal permits (USACE 404), or federal land. Triggers full NEPA review.
            </div>
          </div>
          <Switch
            checked={!!value.federalNexus}
            onCheckedChange={(b) => set('federalNexus', b)}
          />
        </div>

        <div className="flex items-center justify-between" data-testid="pref-field-tribalLandProximityKnown">
          <div>
            <Label className="type-section text-text-secondary">Tribal land proximity known</Label>
            <div className="text-[10px] text-text-muted">
              Tribal land &lt;5mi or known cultural concerns. Triggers NHPA Section 106 consultation.
            </div>
          </div>
          <Switch
            checked={!!value.tribalLandProximityKnown}
            onCheckedChange={(b) => set('tribalLandProximityKnown', b)}
          />
        </div>
      </div>
    </div>
  );
};
